import type { Dayjs } from 'dayjs'
import { orderBy } from 'lodash'
import * as yup from 'yup'

import { DAYS_OF_WEEK, type DayOfWeek } from '../../DaysOfWeekInput'

export type TimePeriod = {
  startTime?: Dayjs | null
  endTime?: Dayjs | null
  daysOfWeek?: DayOfWeek[]
}

/** An uncovered clock window, grouped across days that share the same gap. */
export type UncoveredTimeWindow = {
  daysOfWeek: DayOfWeek[]
  /** Inclusive start, minutes from midnight (0–1440). */
  startMinutes: number
  /** Exclusive end, minutes from midnight (0–1440). */
  endMinutes: number
}

const MIDNIGHT_IN_MINUTES = 24 * 60

/** Safely extract time-of-day as minutes from a Dayjs value. Returns null if the value is invalid. */
function safeTimeToMinutes(time: Dayjs | null | undefined): number | null {
  if (time == null) return null
  try {
    if (typeof time.isValid === 'function' && !time.isValid()) return null
    if (typeof time.hour === 'function' && typeof time.minute === 'function') {
      // Minute resolution: seconds are discarded. A 23:59:59 end and a 23:59:00 end
      // are therefore identical here (both leave a one-minute gap before midnight).
      return time.hour() * 60 + time.minute()
    }
  } catch {
    // Dayjs methods exist but threw — value is unusable
  }
  return null
}

type MinuteRange = { start: number; end: number; index: number }

/**
 * Expand periods into covered [start, end) minute ranges per day of week.
 * Cross-midnight wraps on the same selected day. Equal start/end means all-day.
 * A 00:00 end is treated as end-of-day (24:00).
 */
function getCoveredRangesByDay(periods?: TimePeriod[]): Record<DayOfWeek, MinuteRange[]> {
  const periodsByDay = Object.fromEntries(DAYS_OF_WEEK.map((day) => [day, [] as MinuteRange[]])) as Record<
    DayOfWeek,
    MinuteRange[]
  >

  if (!periods?.length) return periodsByDay

  periods.forEach((period, index) => {
    const startMinutes = safeTimeToMinutes(period.startTime)
    const endMinutesRaw = safeTimeToMinutes(period.endTime)
    if (startMinutes == null || endMinutesRaw == null || !Array.isArray(period.daysOfWeek)) return

    // Treat 00:00 end time as end-of-day (24:00) for coverage / overlap detection
    const endMinutes = endMinutesRaw === 0 ? MIDNIGHT_IN_MINUTES : endMinutesRaw

    period.daysOfWeek.forEach((day: DayOfWeek) => {
      if (startMinutes === endMinutes) {
        // Equal start/end means all-day (midnight–midnight already becomes 0→24:00 above)
        periodsByDay[day].push({ start: 0, end: MIDNIGHT_IN_MINUTES, index })
      } else if (startMinutes > endMinutes) {
        // Cross-midnight wraps on the same selected day (start→midnight and midnight→end)
        periodsByDay[day].push({ start: startMinutes, end: MIDNIGHT_IN_MINUTES, index })
        periodsByDay[day].push({ start: 0, end: endMinutes, index })
      } else {
        periodsByDay[day].push({ start: startMinutes, end: endMinutes, index })
      }
    })
  })

  return periodsByDay
}

/**
 * Gaps in [0, 1440] after merging overlapping/nested covered segments via a
 * running maximum end — consecutive-bar comparison would invent phantom gaps
 * under overlaps. Touching endpoints (current.end === next.start) leave no gap.
 */
function findMinuteGaps(segments: Array<{ start: number; end: number }>): Array<{ start: number; end: number }> {
  if (segments.length === 0) {
    return [{ start: 0, end: MIDNIGHT_IN_MINUTES }]
  }

  const sorted = [...segments].sort((a, b) => a.start - b.start)
  const gaps: Array<{ start: number; end: number }> = []
  let coveredUntil = 0

  for (const segment of sorted) {
    if (segment.start > coveredUntil) {
      gaps.push({ start: coveredUntil, end: segment.start })
    }
    coveredUntil = Math.max(coveredUntil, segment.end)
  }

  if (coveredUntil < MIDNIGHT_IN_MINUTES) {
    gaps.push({ start: coveredUntil, end: MIDNIGHT_IN_MINUTES })
  }

  return gaps
}

/**
 * Merge a same-day pair of gaps that meet at midnight (…→1440 and 0→…) into one
 * overnight window. A sole 0–1440 gap is left alone.
 */
function coalesceMidnightWrapGaps(gaps: Array<{ start: number; end: number }>): Array<{ start: number; end: number }> {
  if (gaps.length < 2) return gaps

  const morningIndex = gaps.findIndex((gap) => gap.start === 0)
  const eveningIndex = gaps.findIndex((gap) => gap.end === MIDNIGHT_IN_MINUTES)
  if (morningIndex === -1 || eveningIndex === -1 || morningIndex === eveningIndex) {
    return gaps
  }

  const morning = gaps[morningIndex]!
  const evening = gaps[eveningIndex]!

  return gaps
    .filter((_, index) => index !== morningIndex && index !== eveningIndex)
    .concat({ start: evening.start, end: morning.end })
}

function windowKey(startMinutes: number, endMinutes: number): string {
  return `${startMinutes}-${endMinutes}`
}

/**
 * Uncovered clock windows for the given periods, grouped across days that share
 * the same gap hours. Seconds are discarded (minute resolution) so a `23:59:59`
 * end is treated like `23:59` and leaves a one-minute hole before midnight.
 *
 * This is informational — gaps are legal (a free window is expressed this way) —
 * so callers should warn / require acknowledgment rather than fail validation.
 */
export function findUncoveredTimePeriods(periods?: TimePeriod[]): UncoveredTimeWindow[] {
  const periodsByDay = getCoveredRangesByDay(periods)
  const groups = new Map<string, UncoveredTimeWindow>()

  for (const day of DAYS_OF_WEEK) {
    const gaps = coalesceMidnightWrapGaps(findMinuteGaps(periodsByDay[day]))
    for (const gap of gaps) {
      const key = windowKey(gap.start, gap.end)
      const existing = groups.get(key)
      if (existing) {
        existing.daysOfWeek.push(day)
        continue
      }
      groups.set(key, {
        daysOfWeek: [day],
        startMinutes: gap.start,
        endMinutes: gap.end
      })
    }
  }

  return orderBy(
    [...groups.values()],
    [(window) => window.startMinutes, (window) => DAYS_OF_WEEK.indexOf(window.daysOfWeek[0]!)],
    ['asc', 'asc']
  )
}

export default function validateNoOverlappingTimes(periods?: TimePeriod[]) {
  if (!periods || periods.length <= 1) return true // No overlap possible with 0 or 1 periods

  const periodsByDay = getCoveredRangesByDay(periods)

  // Check for overlaps within each day
  for (const day of DAYS_OF_WEEK) {
    const dayPeriods = periodsByDay[day]
    if (!dayPeriods.length) continue

    // Sort periods by start time
    dayPeriods.sort((a, b) => a.start - b.start)

    // Check for overlaps between consecutive periods
    for (let i = 0; i < dayPeriods.length - 1; i++) {
      const current = dayPeriods[i]
      const next = dayPeriods[i + 1]
      if (!current || !next) continue

      // Skip if comparing the same period (defensive)
      if (current.index === next.index) continue

      // Overlap if current's end is greater than next's start (touching endpoints allowed)
      if (current.end > next.start) {
        return false
      }
    }
  }

  return true
}

declare module 'yup' {
  // This interface requires the type to match exactly as defined in yup
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ArraySchema<TIn, TContext, TDefault, TFlags> {
    /**
     * Validates that time periods do not overlap within the same day.
     * Touching endpoints are allowed. 00:00–00:00 is treated as all-day.
     * Cross-midnight periods (e.g. 22:00–02:00) are split into two segments for overlap detection.
     */
    noOverlappingTimePeriods(): this
  }
}

yup.addMethod(yup.array, 'noOverlappingTimePeriods', function () {
  return this.test('validateNoOverlappingTimePeriods', validateNoOverlappingTimes)
})
